import React from "react";
import styled from "styled-components";
import CaseStudiesCard from "../CaseStudiesCard";
import Heading from "../../../Heading";
import ImageOne from "../../../../img/casestudy/ai/org-flow.png";
import ImageTwo from "../../../../img/casestudy/ai/api-knowledge.png";
import ImageThree from "../../../../img/casestudy/ai/agent.png";
import Persona from "../../../../img/casestudy/ai/org-agent-personas.png";
import Tooling from "../../../../img/casestudy/ai/api-key.png";
import ProductDesigner from "../../../../img/casestudy/ai/product-knowledge.png";
import ProductManager from "../../../../img/casestudy/ai/product-manager-knowledge.png";
import Support from "../../../../img/casestudy/ai/support-knowledge.png";

import Box from "../../../Box";
import Rule from "../../../Rule";
import Stack from "../../../Stack";
import Links from "../../../Links";
import Image from "../../../Image";
import BlogLayout from "../BlogLayout";

const Message = styled.div`
  width: 100%;
  border-radius: 8px;
  padding: var(--spacing-small);
  background: rgba(0 0 0 / 0.2);
  display: flex;
  gap: var(--spacing-x-small);
  && {
    margin-bottom: var(--spacing-medium);
  }
  p {
    margin: 0;
    font-size: var(--font-size-text-small);
  }
`;

const OrgAgent = (
  <CaseStudiesCard id="orgagent">
    <Heading
      variant="smaller"
      style={{ marginTop: 0, marginBottom: "var(--spacing-x-tiny)", textTransform: "uppercase" }}
    >
      ARTICLE
    </Heading>
    <p style={{ marginBottom: 0, fontSize: "var(--font-size-text-medium)" }}>
      Exploring how AI and agents can assist humans, enhancing workflows without replacing them.
    </p>
    <Rule spacing="medium" inverted mt="var(--spacing-medium)" mb="var(--spacing-large)" />

    <BlogLayout>
      <Stack spacing="var(--spacing-xx-large)">
        <Box>
          <Heading variant="larger" mb="0">
            Role-Aware Org Agents
          </Heading>
          <p style={{ maxWidth: "46ch" }}>
            Agents powered by your organisation’s standards, insights, tooling, and platform data.
          </p>

          <Message>
            {" "}
            <span role="img" aria-label="Read more on Medium">
              📖
            </span>
            <Stack disableMargin>
              <Heading variant="smallest" mt="0" mb="var(--spacing-tiny)" color="white">
                Continue Reading on Medium
              </Heading>
              <p>
                Thanks for checking out this post. You can also read it on{" "}
                <Links
                  href="https://medium.com/@trilfordm/role-aware-org-agents-25edc4f48528?source=friends_link&sk=60252b67d9f55a362be1d607b7d06c23"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Medium
                </Links>
                . Feel free to leave a comment, share your thoughts, or save it for later. I’d love to hear what you
                think.
              </p>
            </Stack>
          </Message>
          <p>
            As a Design System enthusiast fascinated by how AI might assist in the Design System space, I often think
            about how system knowledge could be surfaced and used by agents. Over time, I began to wonder what other
            departments could contribute. What if guidelines, insights, and expertise from across the organisation could
            feed into a single, secure internal agent?
          </p>
          <p>
            This line of thinking led to the concept of Org Agents, tools designed to help employees stay aligned with
            organisational standards while working smarter.
          </p>
          <p>
            In a near-future workplace, Org Agents would connect internal Org Knowledge APIs, including brand standards,
            UX guidelines, product metrics, and support insights, to deliver actionable guidance. They help employees
            work more efficiently, not by replacing people but by enabling organisation-specific ways of working.
          </p>
          <p>
            By exposing internal expertise, Org Agents could unlock knowledge, reduce duplication, and accelerate
            decision-making. Imagine an agent that understands your design standards, communicates in your brand voice,
            surfaces customer issues, and provides real-time product metrics. Harnessing organisational knowledge with
            AI in this way could make insights actionable and tailored to each role.
          </p>
        </Box>

        <Box>
          <Heading variant="large">Org Agent Flow</Heading>
          <p>
            Visual representation of how Org Knowledge APIs feed into the Org Agent, which surfaces insights through the
            Agent Hub or connects to third-party tools.
          </p>
          <Image dark src={ImageOne} alt="Org agent flow chart" caption="Org agent flow chart" />
          <p>
            Next, we will explore how Org Agents make this possible across four areas: Org Knowledge APIs, Org Agent
            Hub, Role-aware Agents, and Tool Integration.
          </p>
        </Box>

        <Box>
          <Heading variant="large">Org Knowledge APIs</Heading>
          <p>
            To be useful, organisational knowledge must be structured and accessible. Org Knowledge APIs turn
            guidelines, data, and expertise into building blocks agents can act on.
          </p>
          <p>
            Valuable knowledge across branding, product, and support is often siloed. Org Knowledge APIs connect these
            insights to a secure agent to provide role-aware guidance.
          </p>

          <Image dark src={ImageTwo} alt="Org Knowledge API's" />
        </Box>

        <Box>
          <Heading variant="large">Org Agent Hub</Heading>
          <p>
            The Org Agent Hub lets teams interact with agents, explore insights, bounce ideas with AI Personas, and
            access tools, all while leveraging Org Knowledge APIs to ensure solutions align with the company’s mission
            and values.
          </p>

          <Image dark src={ImageThree} alt="Org Agent Hub" />
        </Box>

        <Box>
          <Heading variant="large">Role-aware Agents</Heading>
          <p style={{ marginBottom: "var(--spacing-large)" }}>
            Role-aware agents provide guidance and let employees explore different perspectives. For example, an
            employee looking to upskill from Product Designer to Product Manager can start using the PM Agent to test
            scenarios and begin learning new skill sets.
          </p>
          <Heading variant="medium" mb="0">
            Product Design
          </Heading>
          <p>
            Accelerates prototyping and ideation by drawing on internal knowledge such as brand, UX guidelines, and AI
            Personas tailored to your organisation. Paired with tools like Cursor, it keeps prototypes aligned with your
            company’s vision and DNA.
          </p>

          <Image dark src={ProductDesigner} alt="Product Design Profile" />

          <Heading variant="medium" mb="0">
            Product Manager
          </Heading>
          <p>
            Guides strategic decisions by drawing on KPIs, market insights, and organisational knowledge. Helps
            prioritise initiatives, model outcomes, and align teams to drive measurable business impact.
          </p>

          <Image dark src={ProductManager} alt="Product Manager Profile" />

          <Heading variant="medium" mb="0">
            Customer Support
          </Heading>
          <p>
            Empowers fast, accurate, and empathetic responses by leveraging internal knowledge and AI Personas. Improves
            customer satisfaction while maintaining brand voice and consistency.
          </p>

          <Image dark src={Support} alt="Customer Support Profile" />
        </Box>

        <Box>
          <Heading variant="large">Tool Integration</Heading>
          <p>
            Agents are most effective when integrated into the tools employees already use. Connecting an Org Agent to
            approved platforms via a secure API key brings organisational knowledge into everyday workflows. For
            example, using Cursor turns prompts into code prototypes that can be tested with customers.
          </p>

          <Image dark src={Tooling} alt="Tool integration" />
        </Box>
        <Box>
          <Heading variant="large">AI Personas</Heading>
          <p>
            Org Agents surface organisational knowledge, but teams also need insight into how users might respond. AI
            Personas are dynamic user representations powered by product feedback and market data. They help identify
            discovery points, test flows, and simulate real-world friction, allowing teams to refine ideas and validate
            decisions before actual usability tests.
          </p>

          <p>
            Within the LLM, users can interact with AI Personas to bounce ideas, whether to get feedback on a flow or
            explore an upcoming domain of focus for a design solution. These tools complement, not replace, traditional
            user research and discovery practices.
          </p>

          <Image dark src={Persona} alt="AI Persona" />
        </Box>
        <Box>
          <Heading variant="large">Getting Started</Heading>
          <p style={{ marginBottom: "var(--spacing-large)" }}>
            Org Agents aren’t just for organisations with mature systems; they can help smaller teams begin building
            structured knowledge and scalable practices. You don’t need documentation across every department; a simple
            initial knowledge base can already deliver value. Focus on essentials that make the agent genuinely useful.
          </p>
          <Heading variant="medium" mb="var(--spacing-small)">
            Essential Knowledge Areas
          </Heading>
          <ul style={{ marginBottom: "var(--spacing-large)" }}>
            <li>
              <strong>Mission and Values:</strong> Why the organisation exists and how decisions are made. Guides agent
              responses and ensures alignment.
            </li>
            <li>
              <strong>Brand Basics:</strong> Logo usage, color palette, and tone of voice. Maintains consistency as the
              team grows.
            </li>
            <li>
              <strong>Customer Context:</strong> Key customers, pain points, and feedback patterns. Keeps every role
              aligned with customer needs.
            </li>
            <li>
              <strong>Tech Stack:</strong> What you build with and why. Speeds up onboarding and clarifies the technical
              landscape.
            </li>
          </ul>
          <Heading variant="medium" mb="var(--spacing-small)">
            Growing With Your Organisation
          </Heading>
          <p>
            New departments can inherit the core knowledge and layer in their own expertise. This structure becomes a
            template for growth, capturing knowledge as it becomes relevant rather than waiting for perfect
            documentation.
          </p>
        </Box>

        <Box>
          <Heading variant="large" mb="var(--spacing-small)">
            Looking Ahead
          </Heading>
          <p>
            Org Agents are not about replacing people. They amplify expertise by connecting organisational knowledge to
            AI, allowing employees to focus on creativity, judgment, and context while surfacing the right insights at
            the right time.
          </p>
          <p>
            Implementing Org Agents requires collaboration across divisions to make knowledge connected, shareable, and
            actionable, empowering employees to work smarter and more aligned with organisational goals.
          </p>
        </Box>
      </Stack>
    </BlogLayout>
  </CaseStudiesCard>
);

export default OrgAgent;
